import { useQuery } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import checkToken from '../../util/auth/checkToken'
import useStore from '../../util/store/auth'
import useDashboardListFilters, {
  type DashboardDateMode,
} from '../../util/useDashboardListFilters'
import { getDashboardTimezone, getDateHourInTimezone, parseIsoToMs, toIsoRangeFromDateOnly } from '../../util/dateUtils'
import type { DashboardSortableTableOptions } from '@counsel-project/counsel-audit-ai-api'

type DashboardFilterShape = {
  fromDate?: string
  toDate?: string
  dateMode?: DashboardDateMode
  noDateOfService?: boolean
  graphGranularityDateRange?: { from: string; to: string } | null
  selectedDateRange?: { from: string; to: string }
  userEmail?: string
  detailViewClinicianEmail?: string | null
  patientMrn?: string
  detailViewPatientMrn?: string | null
  measures?: string[]
  location?: string
  program?: string
  selectedSeverity?: number | null
  page?: number
  limit?: number
}

type DashboardTablePaginationMode = 'local' | 'url'

type DashboardTableHookFactoryConfig<TResponse, TOptions extends object> = {
  buildQueryKey: (
    directoryId: string,
    filters: {
      page: number
      limit: number
      sortBy?: DashboardSortableTableOptions['sortBy']
      sortOrder?: DashboardSortableTableOptions['sortOrder']
      fromDate?: string
      toDate?: string
      dateField?: string
      timezone?: string
      noVisitDate?: boolean
      severity?: number
      clinicianEmail?: string
      patientMrn?: string
      location?: string
      program?: string
      measureIds?: string[]
    }
  ) => readonly unknown[]
  request: (request: DashboardSortableTableOptions) => Promise<TResponse>
  defaultRowsPerPage?: number
  paginationMode?: DashboardTablePaginationMode
  getOptionPage?: (options: TOptions) => number | undefined
  getOptionLimit?: (options: TOptions) => number | undefined
  getOptionRowsPerPage?: (options: TOptions) => number | undefined
  getOptionSortBy?: (options: TOptions) => DashboardSortableTableOptions['sortBy'] | undefined
  getOptionSortOrder?: (options: TOptions) => DashboardSortableTableOptions['sortOrder'] | undefined
  enableSorting?: boolean
}

const VALID_SEVERITIES = new Set([1, 2, 3])
const DASHBOARD_SORTABLE_SORT_BY_VALUES: ReadonlySet<string> = new Set([
  'documentAt',
  'mostRecentDocumentAt',
  'dateOfService',
  'documentTitle',
  'patientName',
  'clinicianName',
  'totalDocuments',
  'totalAlerts',
  'severity1',
  'severity2',
  'severity3',
])

const normalizeOptionalFilterValue = (value?: string | null) => {
  if (value === null || value === undefined) return undefined
  const trimmedValue = value.trim()
  return trimmedValue || undefined
}

const toCanonicalIso = (value?: string) => {
  const ms = parseIsoToMs(value)
  return ms === undefined ? undefined : new Date(ms).toISOString()
}

const toCanonicalDateRange = (fromDate?: string, toDate?: string) => ({
  fromDate: toCanonicalIso(fromDate),
  toDate: toCanonicalIso(toDate),
})

/** Returns normalized clinician/patient/scope filters for dashboard data queries. */
export const getDashboardScopeFilters = (filters: DashboardFilterShape) => {
  const clinicianEmail =
    normalizeOptionalFilterValue(filters.userEmail) ??
    normalizeOptionalFilterValue(filters.detailViewClinicianEmail)
  const patientMrn =
    normalizeOptionalFilterValue(filters.patientMrn) ??
    normalizeOptionalFilterValue(filters.detailViewPatientMrn)
  const measureIds = filters.measures

  return {
    clinicianEmail,
    patientMrn,
    measureIds,
    location: filters.location,
    program: filters.program,
  }
}

/** Produces a stable ordered measure list for query-key caching only. */
export const getSortedMeasureIdsForQueryKey = (measureIds?: string[]) =>
  measureIds ? [...measureIds].sort((a, b) => a.localeCompare(b)) : undefined

/** Builds the canonical ISO date range directly from calendar date filters. */
export const getCalendarDateRange = (filters: DashboardFilterShape, timezone?: string) => {
  const range = toIsoRangeFromDateOnly(filters.fromDate, filters.toDate, timezone)
  // Invalidate an inverted (From > To) calendar range so no route derived from the
  // user-selected date range fires a request for an impossible window. Both bounds are
  // canonical UTC ISO strings, so the lexicographic compare is chronological.
  if (range.fromDate && range.toDate && range.fromDate > range.toDate) {
    return { fromDate: undefined, toDate: undefined }
  }
  return range
}

/** Converts optional calendar dates into a selected range object when both bounds are valid. */
export const getSelectedRangeFromCalendarDates = (
  fromDate?: string,
  toDate?: string,
  timezone?: string
) => {
  const range = toIsoRangeFromDateOnly(fromDate, toDate, timezone)
  if (!range.fromDate || !range.toDate) return undefined
  return { from: range.fromDate, to: range.toDate }
}

/** Resolves date range using graph granularity range when valid, else falls back to calendar range. */
export const getGraphAwareDateRange = (filters: DashboardFilterShape, timezone?: string) => {
  const graphRangeFrom = filters.graphGranularityDateRange?.from
  const graphRangeTo = filters.graphGranularityDateRange?.to
  const { fromDate: canonicalGraphRangeFrom, toDate: canonicalGraphRangeTo } = toCanonicalDateRange(
    graphRangeFrom,
    graphRangeTo
  )
  const hasGraphRange = canonicalGraphRangeFrom !== undefined && canonicalGraphRangeTo !== undefined

  if (hasGraphRange) {
    return {
      graphRangeFrom,
      graphRangeTo,
      hasGraphRange,
      fromDate: canonicalGraphRangeFrom,
      toDate: canonicalGraphRangeTo,
    }
  }

  return {
    graphRangeFrom,
    graphRangeTo,
    hasGraphRange,
    ...getCalendarDateRange(filters, timezone),
  }
}

/** Returns resolved date bounds plus epoch values and the active source for hook query gating. */
export const getResolvedDateRange = (filters: DashboardFilterShape, timezone?: string) => {
  const graphAware = getGraphAwareDateRange(filters, timezone)
  const fromMs = parseIsoToMs(graphAware.fromDate)
  const toMs = parseIsoToMs(graphAware.toDate)
  const hasDateRange = fromMs !== undefined && toMs !== undefined && fromMs <= toMs

  if (!hasDateRange) {
    return {
      fromDate: undefined,
      toDate: undefined,
      fromMs: undefined,
      toMs: undefined,
      hasDateRange: false,
      source: graphAware.hasGraphRange ? 'graph' : 'calendar',
    } as const
  }

  return {
    fromDate: graphAware.fromDate,
    toDate: graphAware.toDate,
    fromMs,
    toMs,
    hasDateRange: true,
    source: graphAware.hasGraphRange ? 'graph' : 'calendar',
  } as const
}

/** Indicates whether filters currently satisfy date-range mode or paging mode requirements. */
export const hasDateRangeOrPagingMode = (filters: DashboardFilterShape, timezone?: string) => {
  const { hasDateRange } = getResolvedDateRange(filters, timezone)
  const hasPagingMode = !!filters.page && !!filters.limit

  return { hasDateRange, hasPagingMode }
}

/** Extracts date/hour from selected graph range start in the active timezone. */
export const getSelectedRangeDateHour = (filters: DashboardFilterShape, timezone?: string) => {
  const selectedRangeStartMs = parseIsoToMs(filters.selectedDateRange?.from)
  if (selectedRangeStartMs === undefined) return { date: undefined, hour: undefined }
  return getDateHourInTimezone(selectedRangeStartMs, timezone ?? 'UTC')
}

/** Validates dashboard severity to allowed values (1-3) and normalizes invalid values to undefined. */
export const getValidDashboardSeverity = (severity?: number | null) => {
  if (severity === null || severity === undefined) return undefined
  return VALID_SEVERITIES.has(severity) ? severity : undefined
}

/** Validates sortBy values against supported dashboard sortable table fields. */
export const isDashboardSortableSortBy = (
  value: string | undefined
): value is DashboardSortableTableOptions['sortBy'] =>
  value !== undefined && DASHBOARD_SORTABLE_SORT_BY_VALUES.has(value)

/** Creates dashboard table hooks with shared date/scope/query/pagination behavior. */
export const createDashboardTableHook = <TResponse, TOptions extends object = object>({
  buildQueryKey,
  request,
  defaultRowsPerPage = 10,
  paginationMode = 'local',
  getOptionPage,
  getOptionLimit,
  getOptionRowsPerPage,
  getOptionSortBy,
  getOptionSortOrder,
  enableSorting = true,
}: DashboardTableHookFactoryConfig<TResponse, TOptions>) => {
  const useDashboardTable = (options?: TOptions) => {
    const normalizedOptions = (options ?? {}) as TOptions
    const { directory } = useStore()
    const { filters, updateFilters } = useDashboardListFilters()
    const timezone = getDashboardTimezone()
    const {
      scopeFilters: { clinicianEmail, patientMrn, measureIds, location, program },
      resolvedDateRange: { fromDate: actualFromDate, toDate: actualToDate, hasDateRange },
      availability: { hasPagingMode },
      validatedSeverity,
    } = getDashboardQueryContext(filters, timezone)
    const sortedMeasureIdsForQueryKey = getSortedMeasureIdsForQueryKey(measureIds)

    const optionPage = getOptionPage?.(normalizedOptions)
    const optionLimit = getOptionLimit?.(normalizedOptions)
    const optionRowsPerPage = getOptionRowsPerPage?.(normalizedOptions)
    let sortBy: DashboardSortableTableOptions['sortBy'] | undefined
    let sortOrder: DashboardSortableTableOptions['sortOrder'] | undefined
    if (enableSorting) {
      const optionSortBy = getOptionSortBy?.(normalizedOptions)
      sortBy =
        optionSortBy ??
        (isDashboardSortableSortBy(filters.sortBy) ? filters.sortBy : undefined)
      sortOrder = getOptionSortOrder?.(normalizedOptions) ?? filters.sortOrder
    }

    const isUrlPagination = paginationMode === 'url'
    const [localCurrentPage, setLocalCurrentPage] = useState(optionPage ?? 1)
    const rowsPerPage =
      optionRowsPerPage ??
      optionLimit ??
      (isUrlPagination ? filters.limit : undefined) ??
      defaultRowsPerPage

    const currentPage = isUrlPagination ? optionPage ?? filters.page ?? 1 : localCurrentPage
    const setCurrentPage = useCallback(
      (nextPage: number) => {
        if (isUrlPagination) {
          if (optionPage !== undefined) return
          updateFilters({ page: nextPage })
          return
        }
        setLocalCurrentPage(nextPage)
      },
      [isUrlPagination, optionPage, updateFilters]
    )

    const page = currentPage
    const limit = optionLimit ?? rowsPerPage

    const dateField = filters.dateMode
    const noVisitDate = filters.noDateOfService || undefined

    const queryKeyFilters = {
      page,
      limit,
      fromDate: actualFromDate,
      toDate: actualToDate,
      dateField,
      // The range boundaries are instants; the server needs this to read the
      // picked calendar days back out of them in date-of-service mode.
      timezone,
      noVisitDate,
      severity: validatedSeverity,
      clinicianEmail: clinicianEmail ?? undefined,
      patientMrn: patientMrn ?? undefined,
      location,
      program,
      measureIds: sortedMeasureIdsForQueryKey,
      ...(enableSorting ? { sortBy, sortOrder } : {}),
    }

    const queryKey = buildQueryKey(directory?._id ?? '', queryKeyFilters)

    const queryResult = useQuery<TResponse>({
      queryKey,
      queryFn: async () => {
        await checkToken()
        const requestPayload: DashboardSortableTableOptions = {
          token: '',
          directoryId: directory?._id ?? '',
          page,
          limit,
          severity: validatedSeverity,
          fromDate: actualFromDate ?? '',
          toDate: actualToDate ?? '',
          dateField,
          timezone,
          noVisitDate,
          clinicianEmail: clinicianEmail ?? undefined,
          patientMrn: patientMrn ?? undefined,
          location,
          program,
          measureIds,
          ...(enableSorting
            ? {
                sortBy,
                sortOrder: sortOrder ?? undefined,
              }
            : {}),
        }

        return request(requestPayload)
      },
      enabled: !!directory?._id && (hasDateRange || hasPagingMode),
    })

    return {
      ...queryResult,
      currentPage,
      setCurrentPage,
      rowsPerPage,
    }
  }

  return useDashboardTable
}

/** Produces the shared normalized filter/query context consumed across dashboard data hooks. */
export const getDashboardQueryContext = (filters: DashboardFilterShape, timezone?: string) => {
  const graphAwareDateRange = getGraphAwareDateRange(filters, timezone)
  const resolvedDateRange = getResolvedDateRange(filters, timezone)
  return {
    scopeFilters: getDashboardScopeFilters(filters),
    calendarDateRange: getCalendarDateRange(filters, timezone),
    graphAwareDateRange,
    resolvedDateRange,
    availability: hasDateRangeOrPagingMode(filters, timezone),
    selectedRangeDateHour: getSelectedRangeDateHour(filters, timezone),
    validatedSeverity: getValidDashboardSeverity(filters.selectedSeverity),
  }
}
